class MultiProductSubscriptions extends HTMLElement {
  constructor() {
    super();
    this.multiProductSubscriptionData = JSON.parse(
      document.getElementById("multi-product-subscription-data").textContent
    );

    this.cart = document.querySelector("cart-drawer");
    this.flushDialog = document.querySelector("#how-many-flush-dialog");
  }

  // Called when the element is inserted into a document.
  connectedCallback() {
    this.section = this;

    this.variantId = document.querySelector("#variant-id");
    this.sellingPlan = document.querySelector("#selling-plan");

    this.variantSelectors = this.section.querySelector("#variant-selectors");
    this.productOptionSelector = this.section.querySelector(
      "#product-option-selector"
    );

    this.subscriptionSelectors = this.section.querySelector(
      "#subscription-selectors"
    );

    this.addToCartBtn = this.section.querySelector(
      "#multi-product-subscriptions--atc"
    );

    this.loadModule();

    if (this.variantSelectors) {
      this.variantSelectors.addEventListener(
        "click",
        this.variantSelectorsEventHandler.bind(this)
      );
    }

    if (this.productOptionSelector) {
      this.productOptionSelector.addEventListener(
        "click",
        this.productOptionSelectorEventHandler.bind(this)
      );
    }

    this.subscriptionSelectors.addEventListener(
      "click",
      this.subscriptionSelectorsEventHandler.bind(this)
    );

    this.addToCartBtn.addEventListener("click", this.addToCart.bind(this));

    // Dispatch the custom event after everything is set up
    this.dispatchEvent(
      new CustomEvent("MultiProductSubscriptions:connected", {
        bubbles: true, // Set true to bubble up through the DOM
        cancelable: true, // Set true to allow canceling the event
      })
    );

    this.openHowManyFlushDialogBtn = this.querySelector("#how-many-flush");
    this.openHowManyFlushDialogBtn?.addEventListener("click", () => {
      this.flushDialog.show();
    });
  }

  // Called when the element is removed from a document.
  disconnectedCallback() {
    this.productOptionSelector.removeEventListener(
      "click",
      this.productOptionSelectorEventHandler.bind(this)
    );

    this.variantSelectors.removeEventListener(
      "click",
      this.variantSelectorsEventHandler.bind(this)
    );

    this.subscriptionSelectors.removeEventListener(
      "click",
      this.subscriptionSelectorsEventHandler.bind(this)
    );
  }

  /**
   * Handles click events on variant selectors.
   * @param {Event} event - The click event.
   */
  variantSelectorsEventHandler(event) {
    const currentSelectedBtn = event.target.closest("button");
    if (!currentSelectedBtn) return;
    const { handle } = currentSelectedBtn.dataset;
    const previousSelectedBtn = this.variantSelectors.querySelector(
      `[data-variant-selected="true"]`
    );
    previousSelectedBtn.dataset.variantSelected = false;
    currentSelectedBtn.dataset.variantSelected = true;

    this.loadModule(handle);
  }

  productOptionSelectorEventHandler(event) {
    const currentSelectedBtn = event.target.closest("button");
    if (!currentSelectedBtn) return;

    const selectorsContainer = currentSelectedBtn.closest(
      ".selectors-container"
    );
    const previousSelectedBtn = selectorsContainer.querySelector(
      `[data-variant-selected="true"]`
    );
    previousSelectedBtn.dataset.variantSelected = false;
    currentSelectedBtn.dataset.variantSelected = true;

    this.loadModule();
    const { optionHandle } = currentSelectedBtn.dataset;
    this.filterMediaGalleryByVariant(optionHandle);
  }

  /**
   * Handles click events on subscription selectors.
   * @param {Event} event - The click event.
   */
  subscriptionSelectorsEventHandler(event) {
    const currentSelectedSubscription = event.target.closest(
      ".subscription-selector"
    );
    if (!currentSelectedSubscription) return false;
    const previousSelectedSubscription =
      this.subscriptionSelectors.querySelector(
        `[data-subscription-selector-selected="true"]`
      );
    previousSelectedSubscription.dataset.subscriptionSelectorSelected = false;
    currentSelectedSubscription.dataset.subscriptionSelectorSelected = true;

    this.updateSubscriptionTagContent(currentSelectedSubscription);

    this.loadModule();
  }

  /**
   * Toggles the visibility of media carousels based on the selected variant.
   * @param {string} handle - The handle of the variant selector.
   */
  filterMediaGalleryByVariant(handle) {
    const mediaGalleryElm = document.querySelector(".mediaGallery-container");
    const altTextList = mediaGalleryElm?.getAttribute("data-alt-text-list");
    if (mediaGalleryElm && altTextList.indexOf(handle) > -1) {
      mediaGalleryElm.setAttribute("data-active-variant-handle", handle);
    }
  }

  /**
   * Loads the module with the specified handle.
   * @param {string} handle - The handle of the variant selector.
   */
  loadModule(handle = "") {
    const currentSelectedVariantHandle =
      handle || this.getSelectedVariantSelectorHandle();

    const productVariant = this.getProductVariantByHandle(
      currentSelectedVariantHandle
    );

    const variantSku = productVariant.sku;

    const multiProductVariant = this.getMultiProductVariantByHandleOrSku(
      currentSelectedVariantHandle,
      variantSku
    );

    const subscriptionSelected = this.getSelectedSubscription();

    this.updateVariantAndSellingPlanId(
      subscriptionSelected,
      productVariant,
      multiProductVariant
    );

    this.updateSubscriptionSelectorsPrices(productVariant, multiProductVariant);
  }

  /**
   * Updates the variant ID and selling plan ID based on subscription selection.
   * @param {string} subscriptionSelected - The selected subscription type.
   * @param {Object} productVariant - The product variant object.
   * @param {Object} multiProductVariant - The multi-product variant object.
   */
  updateVariantAndSellingPlanId(
    subscriptionSelected,
    productVariant,
    multiProductVariant
  ) {
    switch (subscriptionSelected) {
      case "multi-product": {
        this.variantId.value = multiProductVariant.id;
        this.sellingPlan.value = this.getMultiProductSellingPlanId();
        break;
      }
      case "default-selling-plan": {
        this.variantId.value = productVariant.id;
        this.sellingPlan.value = this.getProductSellingPlanId();
        break;
      }
      case "one-time-purchase": {
        this.variantId.value = productVariant.id;
        this.sellingPlan.value = "";
        break;
      }
      default:
        break;
    }
  }

  /**
   * Updates the prices displayed on subscription selectors.
   * @param {Object} productVariant - The product variant object.
   * @param {Object} multiProductVariant - The multi-product variant object.
   */
  updateSubscriptionSelectorsPrices(productVariant, multiProductVariant) {
    const {
      sellingPlanPrice: multiProductVariantSellingPlanPrice,
      price: multiProductVariantPrice,
      id: multiProductVariantId,
    } = multiProductVariant;

    const productDefaultSellingPlan = this.getDefaultSellingPlan();
    const productRecurrenceAndFrequency =
      this.calculateRecurrencePatternAndFrequency(
        productDefaultSellingPlan.frequencyNumber,
        productDefaultSellingPlan.frequencyPeriod
      );

    const multiProductSellingPlan = this.getMultiProductSellingPlan();
    const multiProductRecurrenceAndFrequency =
      this.calculateRecurrencePatternAndFrequency(
        multiProductSellingPlan.frequencyNumber,
        multiProductSellingPlan.frequencyPeriod
      );

    const {
      sellingPlanPrice: productVariantSellingPlanPrice,
      price: productVariantPrice,
      comparePrice: productVariantComparePrice,
      id: productVariantId,
    } = productVariant;

    this.setSubscriptionSelectorPrices(
      multiProductVariantSellingPlanPrice,
      multiProductVariantPrice,
      multiProductRecurrenceAndFrequency,
      multiProductVariantId,
      productVariantPrice,
      productVariantComparePrice
    );

    this.setDefaultSellingPlanPrices(
      productVariantSellingPlanPrice,
      productVariantPrice,
      productRecurrenceAndFrequency,
      productVariantId,
      productVariantComparePrice
    );

    this.setProductVariantPrice(
      productVariantPrice,
      productVariantComparePrice,
      productVariantId
    );
  }

  /**
   * Updates the content of the subscription tag with the savings percentage.
   * If no savings percentage or subscription tag is found, hides all subscription tags.
   *
   * Note: Hiding all subscription tags is a temporary solution
   * until the Intelligems test is completed. Ideally, there should
   * be only one element of this type.
   *
   * @param {HTMLElement} currentSelectedSubscription Subscription selector
   */
  updateSubscriptionTagContent(currentSelectedSubscription) {
    const savingsPercentage =
      currentSelectedSubscription.querySelector(".savings-pill")?.dataset
        .percentage;
    const subscriptionTag = this.querySelector(".subscription-tag");

    if (!savingsPercentage || savingsPercentage === "0") {
      subscriptionTag.style.display = "none";
      return;
    }
    subscriptionTag.style.display = "block";
    subscriptionTag.innerHTML = `Save ${savingsPercentage}%`;
  }

  /**
   * Adds the selected product to the cart.
   */
  async addToCart() {
    const formData = this.buildFormDataForRequest();

    try {
      this.addToCartBtn.disabled = true;
      this.addToCartBtn.innerHTML = "ADDING TO THE CART";

      const request = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const response = await request.json();

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: "product-form",
        productVariantId: this.getVariantId(),
        cartData: response,
      });

      this.cart.renderContents(response);
    } catch (error) {
      console.log(`❌ Multi Product Subscription | addToCart | ${error}`);
      alert(error);
      if (typeof hj === "function") {
        hj('event', 'error_occurred');
      }
    } finally {
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.innerHTML = "ADD TO CART";
      if (this.cart && this.cart.classList.contains("is-empty"))
        this.cart.classList.remove("is-empty");
    }
  }

  /**
   * Retrieves the selected subscription type.
   * @returns {string} The selected subscription type.
   */
  getSelectedSubscription() {
    return this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-selected="true"]`
    ).dataset.subscriptionSelectorType;
  }

  /**
   * Retrieves the handle of the selected variant selector.
   * @returns {string} The handle of the selected variant selector.
   */
  getSelectedVariantSelectorHandle() {
    if (this.variantSelectors) {
      return this.variantSelectors.querySelector(
        `[data-variant-selected="true"]`
      ).dataset.optionHandle;
    }

    if (this.productOptionSelector) {
      return Array.from(
        this.productOptionSelector.querySelectorAll(
          `[data-variant-selected="true"]`
        )
      )
        .map((el) => el.dataset.optionHandle)
        .join("-");
    }
  }

  /**
   * Retrieves the product variant by handle.
   * @param {string} handle - The handle of the product variant.
   * @returns {Object} The product variant object.
   */
  getProductVariantByHandle(handle) {
    return this.multiProductSubscriptionData.product.variants.find(
      (el) => el.handle === handle
    );
  }

  /**
   * Retrieves the multi-product variant by handle, or if there's no handle match it tries to compare the SKU of the normal product with the SKU of the 3 month supply using the suffix "-90".
   * @param {string} handle - The handle of the multi-product variant.
   * @param {string} sku - The sku of the multi-product variant.
   * @returns {Object} The multi-product variant object.
   */
  getMultiProductVariantByHandleOrSku(handle, sku) {
    return this.multiProductSubscriptionData.multiProductSubscription.variants.find(
      (el) => el.handle === handle || el.sku === sku + "-90"
    );
  }

  /**
   * Retrieve the product's selling plan object
   * @returns {object} Selling plan object
   */
  getDefaultSellingPlan() {
    return this.multiProductSubscriptionData.product.sellingPlan;
  }

  /**
   * Retrieve the multi product's selling plan object
   * @returns {object} Selling plan object
   */
  getMultiProductSellingPlan() {
    return this.multiProductSubscriptionData.multiProductSubscription
      .sellingPlan;
  }

  /**
   * Retrieves the selling plan ID for multi-product subscription.
   * @returns {number} The selling plan ID for multi-product subscription.
   */
  getMultiProductSellingPlanId() {
    return this.multiProductSubscriptionData.multiProductSubscription
      .sellingPlan.id;
  }

  /**
   * Retrieves the selling plan ID for the default product.
   * @returns {number} The selling plan ID for the default product.
   */
  getProductSellingPlanId() {
    return this.multiProductSubscriptionData.product.sellingPlan.id;
  }

  /**
   * Retrieves the variant ID.
   * @returns {number} The variant ID.
   */
  getVariantId() {
    return +this.variantId.value;
  }

  /**
   * Retrieves the selling plan ID.
   * @returns {number} The selling plan ID.
   */
  getSellingPlanId() {
    return +this.sellingPlan.value;
  }

  /**
   * Sets the prices for subscription selectors for multi-product subscription.
   * @param {number} multiProductVariantSellingPlanPrice - The selling plan price for multi-product variant.
   * @param {number} multiProductVariantPrice - The price for multi-product variant.
   * @param {Object} multiProductRecurrenceAndFrequency Selling plan object
   * @param {number} productVariantPrice - The price for product variant.
   */
  setSubscriptionSelectorPrices(
    multiProductVariantSellingPlanPrice,
    multiProductVariantPrice,
    multiProductRecurrenceAndFrequency,
    multiProductVariantId,
    productVariantPrice,
    productVariantComparePrice
  ) {
    const { pattern, months } = multiProductRecurrenceAndFrequency;
    const selector = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="multi-product"]`
    );

    // const priceByFrequency = (
    //   multiProductVariantSellingPlanPrice / months
    // ).toFixed(2);
    const priceByFrequency = this.addZeroToDecimal(
      multiProductVariantSellingPlanPrice / months
    );

    selector.querySelector(
      ".selling-plan-price"
    ).innerHTML = `$${priceByFrequency}${months && " /mo"}`;

    const formattedPrice = this.addZeroToDecimal(
      multiProductVariantSellingPlanPrice
    );

    selector.querySelector(
      ".default-price"
    ).innerHTML = `$<span>${formattedPrice} paid ${pattern}`;

    // Use variant compare price of one-time purchase product, if empty then use one-time purchase price
    selector.querySelector(".selling-plan-compare-price").innerHTML = `$${
      productVariantComparePrice !== 0
        ? productVariantComparePrice
        : productVariantPrice
    }`;
  }

  /**
   * Sets the prices for subscription selectors for default product.
   * @param {number} productVariantSellingPlanPrice - The selling plan price for product variant.
   * @param {number} productVariantPrice - The price for product variant.
   * @param {Object} productRecurrenceAndFrequency Selling plan object
   */
  setDefaultSellingPlanPrices(
    productVariantSellingPlanPrice,
    productVariantPrice,
    productRecurrenceAndFrequency,
    productVariantId,
    productVariantComparePrice
  ) {
    const selector = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="default-selling-plan"]`
    );
    const { pattern, months } = productRecurrenceAndFrequency;

    const fixedProductVariantSellingPlanPrice = this.addZeroToDecimal(
      productVariantSellingPlanPrice
    );

    selector.querySelector(
      ".selling-plan-price"
    ).innerHTML = `$${fixedProductVariantSellingPlanPrice} ${months && "/mo"}`;

    selector
      .querySelector(".selling-plan-price")
      .setAttribute("data-variant-id", productVariantId);

    selector.querySelector(
      ".default-price"
    ).innerHTML = `$<span data-variant-id="${productVariantId}">${fixedProductVariantSellingPlanPrice}</span> paid ${pattern}`;

    // If both the subscription and one-time price are the same, hide the compare price
    if (
      fixedProductVariantSellingPlanPrice === productVariantPrice.toString()
    ) {
      selector.querySelector(".selling-plan-compare-price").innerHTML = "";
      return;
    }
    // Use variant compare price of one-time purchase product, if empty then use one-time purchase price
    selector.querySelector(".selling-plan-compare-price").innerHTML = `$${
      productVariantComparePrice !== 0
        ? productVariantComparePrice
        : productVariantPrice
    }`;
  }

  /**
   * Sets the price for product variant in one-time purchase subscription.
   * @param {number} price - The price for product variant.
   * @param {number} comparePrice - The compare price for product variant.
   */
  setProductVariantPrice(price, comparePrice, productVariantId) {
    const selector = this.subscriptionSelectors.querySelector(
      `[data-subscription-selector-type="one-time-purchase"]`
    );
    selector.querySelector(".selling-plan-price").innerHTML = `$${price}`;

    selector
      .querySelector(".selling-plan-price")
      .setAttribute("data-variant-id", productVariantId);
    // Get compare price from product variant, if empty then set empty inner html
    selector.querySelector(".selling-plan-compare-price").innerHTML =
      comparePrice == 0 ? "" : `$${comparePrice}`;
  }

  /**
   * Calculates the recurrence pattern based on the frequency number and period.
   * @param {number} recurrenceCount - The number of times the event repeats.
   * @param {string} recurrenceUnit - The unit of recurrence, either "week" or "day".
   * @returns {Object} The recurrence pattern.
   */
  calculateRecurrencePatternAndFrequency(frequencyNumber, frequencyPeriod) {
    const weeksInMonth = 4;
    const averageDaysInAMonth = 30.437;
    let pattern = "";
    let months = 0;
    let weeks = 0;

    const determineMonthlyPattern = (months) => {
      if (months === 1) return "monthly";
      if (months === 3) return "quarterly";
      return months > 1 ? `every ${months} months` : "monthly";
    };

    const determineWeeklyPattern = (weeks) => {
      return weeks > 1 ? `every ${frequencyNumber} weeks}` : "weekly";
    };

    if (frequencyPeriod === "week") {
      if (frequencyNumber < weeksInMonth) {
        pattern = determineWeeklyPattern(frequencyNumber);
        weeks = frequencyNumber;
        return { pattern, months, weeks };
      }

      months = frequencyNumber / weeksInMonth;
      pattern = determineMonthlyPattern(months);
      return { pattern, months, weeks };
    }

    if (frequencyPeriod === "day") {
      if (frequencyNumber < averageDaysInAMonth) {
        pattern = `monthly`;
        months = 1;
        return { pattern, months, weeks };
      }

      months = Math.round(frequencyNumber / averageDaysInAMonth);
      pattern = determineMonthlyPattern(months);
      return { pattern, months, weeks };
    }
  }

  addZeroToDecimal(number) {
    // const formattedPrice = Number(number).toLocaleString("en-US", {
    //   minimumFractionDigits: 2,
    //   maximumFractionDigits: 2,
    // });
    // return formattedPrice;

    if (Number.isInteger(number)) {
      return number.toFixed(0); // No decimal places for whole numbers
    } else {
      return number.toFixed(2); // Two decimal places for non-integers
    }
  }

  buildFormDataForRequest() {
    return {
      items: [
        {
          id: this.getVariantId(),
          quantity: 1,
          ...(this.getSellingPlanId() && {
            selling_plan: this.getSellingPlanId(),
          }),
        },
      ],
      sections: this.cart.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };
  }
}
customElements.define("multi-product-subscriptions", MultiProductSubscriptions);
